import { listNoyoDifferences } from 'enrollment-platform/api/partner-benefit-graph/noyo-differences.client'
import type {
  NoyoDifference,
  NoyoDifferencesMeta,
} from 'enrollment-platform/api/partner-benefit-graph/noyo-differences.types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { getErrorMessage } from '../../IdeonConfig/hooks/getErrorMessage'
import {
  DEFAULT_MIN_IMPACT_SCORE,
  DEFAULT_NOYO_ISSUE_CLASSIFICATIONS,
  joinClassifications,
  type NoyoClassification,
} from '../noyoIssueFilters'

const DEFAULT_PAGE_SIZE = 20

export function useNoyoIssues() {
  const [issues, setIssues] = useState<NoyoDifference[]>([])
  const [meta, setMeta] = useState<NoyoDifferencesMeta>({
    offset: 0,
    page_num: 1,
    page_size: DEFAULT_PAGE_SIZE,
    total_records: 0,
  })
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [classifications, setClassifications] = useState<NoyoClassification[]>(
    DEFAULT_NOYO_ISSUE_CLASSIFICATIONS
  )
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const fetchVersionRef = useRef(0)

  const fetchIssues = useCallback(
    async (
      nextPage: number,
      nextPageSize: number,
      nextClassifications: readonly NoyoClassification[]
    ) => {
      const version = ++fetchVersionRef.current
      setLoading(true)
      setLoadError(null)
      try {
        const data = await listNoyoDifferences({
          offset: (nextPage - 1) * nextPageSize,
          pageSize: nextPageSize,
          minImpactScore: DEFAULT_MIN_IMPACT_SCORE,
          isOpen: true,
          classification: joinClassifications(nextClassifications),
        })
        if (version !== fetchVersionRef.current) return
        setIssues(data.response ?? [])
        setMeta(
          data.meta ?? {
            offset: (nextPage - 1) * nextPageSize,
            page_num: nextPage,
            page_size: nextPageSize,
            total_records: data.response?.length ?? 0,
          }
        )
      } catch (err) {
        if (version !== fetchVersionRef.current) return
        console.error(err)
        const message = getErrorMessage(err)
        setLoadError(message)
        toast.error(`Failed to load Noyo issues: ${message}`)
        setIssues([])
        setMeta({
          offset: 0,
          page_num: 1,
          page_size: nextPageSize,
          total_records: 0,
        })
      } finally {
        if (version === fetchVersionRef.current) {
          setLoading(false)
        }
      }
    },
    []
  )

  useEffect(() => {
    void fetchIssues(page, pageSize, classifications)
  }, [fetchIssues, page, pageSize, classifications])

  const refresh = useCallback(
    () => fetchIssues(page, pageSize, classifications),
    [fetchIssues, page, pageSize, classifications]
  )

  const updateClassifications = useCallback(
    (nextClassifications: NoyoClassification[]) => {
      setClassifications(nextClassifications)
      setPage(1)
    },
    []
  )

  const updatePageSize = useCallback((nextPageSize: number) => {
    setPageSize(nextPageSize)
    setPage(1)
  }, [])

  return {
    issues,
    meta,
    page,
    pageSize,
    classifications,
    loading,
    loadError,
    setPage,
    setPageSize: updatePageSize,
    setClassifications: updateClassifications,
    refresh,
  }
}
